import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tour-overlay"
}

import axios from "@/axiosInstance";
import SideBar from "@/components/SideBar.vue";
import {
	computed,
	inject,
	nextTick,
	onBeforeUnmount,
	onMounted,
	provide,
	ref,
	watch,
} from "vue";
import { useRoute } from "vue-router";
import AiPrompt from "./components/AiPrompt.vue";
import { authIsCached, tourMap } from "./router";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const isOpen = ref(false);
const desktop = ref(window.innerWidth >= 1024);
const route = useRoute();

/* ---- tour globale -------------------------------------------------- */
const tours = inject<Record<string, any>>("tours")!; // ricevuto da main.ts
const currentSteps = ref<any[]>([]);
const tourLoading = ref(false); // spinner on/off
// let globalSeen = false; // flag fornito dal server
const seenPages = new Set<string>(); // cache locale => evita chiamate alla stessa pagina

/* la sidebar si mostra solo se la rotta NON ha hideSidebar */
const showSidebar = computed(() => !route.meta.hideSidebar);
const showAiPrompt = computed(() => !route.meta.hideAiPrompt);

const currentUserName = computed(() => {
	try {
		const raw =
			localStorage.getItem("authUser") ||
			localStorage.getItem("user") ||
			localStorage.getItem("profile") ||
			"";
		if (raw) {
			const u = JSON.parse(raw);
			return (
				u?.username ||
				u?.name ||
				(u?.first_name && u?.last_name
					? `${u.first_name} ${u.last_name}`
					: null) ||
				(u?.email ? String(u.email).split("@")[0] : "") ||
				""
			);
		}
	} catch {}
	return "";
});

onMounted(() =>
	window.addEventListener(
		"resize",
		() => (desktop.value = window.innerWidth >= 1024),
	),
);

watch(
	() => route.fullPath,
	() => {
		isOpen.value = false;
	},
);

const sidebarOffset = computed(() => {
	if (!showSidebar.value) return "0px";
	if (desktop.value) return isOpen.value ? "220px" : "60px";
	if (!desktop.value && window.innerWidth >= 768)
		return isOpen.value ? "220px" : "0px";
	return "0px";
});
window.addEventListener(
	"resize",
	() => (desktop.value = window.innerWidth >= 1024),
);

// --- helper: filtra step visibili della pagina corrente
function visiblePageSteps(name: string) {
	const raw = tourMap[name] ?? [];
	return raw.filter((s: any) => !!document.querySelector(s.target));
}

// --- computed per abilitare il bottone in sidebar
const canStartPageTour = computed(() => {
	const name = String(route.name || "");
	return visiblePageSteps(name).length > 0;
});

// --- avvio MANUALE della tour di pagina; ritorna true se parte
async function startPageTour(): Promise<boolean> {
	const name = String(route.name || "");
	if (!name) return false;

	const steps = visiblePageSteps(name);
	if (!steps.length) return false;

	currentSteps.value = steps;
	await nextTick();

	const tour = tours?.globalTour;
	if (!tour || typeof tour.start !== "function") return false;

	tour.start();
	return true;
}

// ——— ESPORTA alla sidebar
provide("startPageTour", startPageTour);
provide("canStartPageTour", canStartPageTour);
/* ② – POST /auth/me/tour { tour:'global', page } */
async function isPageSeen(page: string) {
	if (!authIsCached()) return true;
	if (!page) return true;
	if (seenPages.has(page)) return true;

	try {
		const { data } = await axios.get("/auth/me/tour", {
			params: { tour: "global", page },
		});

		if (data.seen) seenPages.add(page);

		return data.seen;
	} catch (e) {
		console.error("Errore nel ritrovare tour 'globale'", e);
		return true;
	}
	// globalSeen = true;
}

async function markPageSeen(page: string) {
	if (!authIsCached()) return;
	if (!page || seenPages.has(page)) return;
	try {
		await axios.post("/auth/me/tour", { tour: "global", page });
		seenPages.add(page);
	} catch (e) {
		console.error("Errore nel salvare tour 'globale'", e);
	}
}

function startWhenReady(steps: any[]) {
	const everyExists = () =>
		steps.every((s: any) => document.querySelector(s.target));

	const tryStart = () => {
		if (everyExists()) {
			currentSteps.value = steps; // ✔️  prop reattiva

			nextTick(() => {
				// attesa mini-render
				tours.globalTour.start(); // ✔️  parte senza errori
			});
		} else {
			setTimeout(tryStart, 150); // riprova
		}
	};

	tryStart();
}

/* ① – legge dal backend se il tour è già visto */
async function startGlobalTourIfNeeded(page: string) {
	if (!authIsCached() || !page) return;
	if (await isPageSeen(page)) return; // server dice “già visto”

	// routeName passato da handler “route-changed”
	const steps = tourMap[page];
	if (!steps) return;

	startWhenReady(steps);
}

const tourCallbacks = {
	onStart: () => {
		tourLoading.value = true; // overlay ON
		/* Lascia il tempo al portal di vue3-tour di montarsi,
       poi lo togli:                                   */
		nextTick(() => (tourLoading.value = false));
	},
	onFinish: () => {
		markPageSeen(route.name as string);
		// tourLoading.value = false;
	},
	onSkip: () => {
		markPageSeen(route.name as string);
		// tourLoading.value = false;
	},
};

/* ---- avvio manuale (pulsante “Guida rapida” della sidebar) ------- */
provide("startTour", () => {
	const page = route.name as string | undefined;
	if (!page) return; // niente rotta corrente? esci.

	// 1) aggiorna gli step
	currentSteps.value = tourMap[page] ?? [];

	// 2) parte subito ignorando lo "seen"
	nextTick(() => tours.globalTour.start());
});

provide("isDesktop", desktop);
provide("isSidebarOpen", isOpen);

// rende la funzione disponibile ai figli (Sidebar)
// provide("startTour", manualStartTour);

/* evento emesso dal router (afterEach) */
const handler = (e: Event) => {
	const { detail: page } = e as CustomEvent<string>;
	if (page) startGlobalTourIfNeeded(page);
};

onMounted(() => {
	/* 1. listener route-changed (già ok) */
	window.addEventListener("route-changed", handler);

	/* 2. listener resize (una sola volta) */
	window.addEventListener("resize", () => {
		desktop.value = window.innerWidth >= 1024;
	});

	/* 3. avvio tour iniziale */
	tourLoading.value = true;
	try {
		const first = route.name as string | undefined;
		if (authIsCached() && first) startGlobalTourIfNeeded(first);
	} finally {
		tourLoading.value = false;
	}
});

onBeforeUnmount(() => {
	window.removeEventListener("route-changed", handler);
	/* stesso per il resize */
	window.removeEventListener("resize", () => {
		desktop.value = window.innerWidth >= 1024;
	});
});


return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!
  const _component_v_tour = _resolveComponent("v-tour")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (tourLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("div", { class: "tour-spinner" }, null, -1)
        ]))]))
      : _createCommentVNode("", true),
    (showSidebar.value)
      ? (_openBlock(), _createBlock(SideBar, {
          key: 1,
          isOpen: isOpen.value,
          desktop: desktop.value,
          onToggle: _cache[0] || (_cache[0] = ($event: any) => (isOpen.value = !isOpen.value))
        }, null, 8, ["isOpen", "desktop"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "page-wrap",
      style: _normalizeStyle({
			'--sb-offset': sidebarOffset.value,
			background: _unref(route).name === 'Data' ? '#0b0e12' : undefined,
			overflow: _unref(route).name === 'LiveMaps' ? 'hidden' : undefined,
			height: _unref(route).name === 'LiveMaps' ? '100dvh' : undefined,
		})
    }, [
      (_openBlock(), _createBlock(_component_router_view, {
        key: _unref(route).fullPath
      }))
    ], 4),
    (showAiPrompt.value)
      ? (_openBlock(), _createBlock(AiPrompt, {
          key: _unref(route).fullPath,
          userName: currentUserName.value
        }, null, 8, ["userName"]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_tour, {
      name: "globalTour",
      steps: currentSteps.value,
      callbacks: tourCallbacks
    }, null, 8, ["steps"])
  ], 64))
}
}

})